import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { FormField, Button } from './ui';
import SearchableSelect from './SearchableSelect';
import {
  ExportField,
  ExportableData,
  flattenDataForExport,
  exportToXLSX,
  exportToCSV,
} from '../utils/exportUtils';
import './ExportModal.css';

type ExportFormat = 'xlsx' | 'csv';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ExportableData[];
  filename: string;
  availableFields: ExportField[];
  title?: string;
  attendanceMap?: Map<string, boolean | null>;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  data,
  filename,
  availableFields,
  title,
  attendanceMap,
}) => {
  const { t } = useTranslation();
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [error, setError] = useState<string | null>(null);

  // Initialize selected fields based on defaultChecked
  useEffect(() => {
    if (isOpen) {
      const defaultSelected = new Set(
        availableFields
          .filter(f => f.defaultChecked)
          .map(f => f.key)
      );
      setSelectedFields(defaultSelected);
      setError(null);
    }
  }, [isOpen, availableFields]);

  const toggleField = (fieldKey: string) => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fieldKey)) {
        newSet.delete(fieldKey);
      } else {
        newSet.add(fieldKey);
      }
      return newSet;
    });
    setError(null);
  };

  const selectAllInCategory = (category: 'member' | 'user' | 'attendance' | 'requirement') => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      availableFields
        .filter(f => f.category === category)
        .forEach(f => newSet.add(f.key));
      return newSet;
    });
  };

  const deselectAllInCategory = (category: 'member' | 'user' | 'attendance' | 'requirement') => {
    setSelectedFields(prev => {
      const newSet = new Set(prev);
      availableFields
        .filter(f => f.category === category)
        .forEach(f => newSet.delete(f.key));
      return newSet;
    });
  };

  // Helper to get display label for a field
  const getFieldLabel = (field: ExportField): string => {
    if (field.labelKey) {
      return t(field.labelKey);
    }
    return field.label || field.key;
  };

  const handleExport = () => {
    if (selectedFields.size === 0) {
      setError(t('export.noFieldsSelected'));
      return;
    }

    const fieldsToExport = availableFields.filter(f => selectedFields.has(f.key));
    const exportData = flattenDataForExport(data, fieldsToExport, attendanceMap, t);

    if (format === 'xlsx') {
      exportToXLSX(exportData, filename);
    } else {
      exportToCSV(exportData, filename);
    }

    onClose();
  };

  const memberFields = availableFields.filter(f => f.category === 'member');
  const userFields = availableFields.filter(f => f.category === 'user');
  const attendanceFields = availableFields.filter(f => f.category === 'attendance');
  const requirementFields = availableFields.filter(f => f.category === 'requirement');

  const renderFieldSection = (
    fields: ExportField[],
    category: 'member' | 'user' | 'attendance' | 'requirement',
    titleKey: string
  ) => {
    if (fields.length === 0) return null;

    const allSelected = fields.every(f => selectedFields.has(f.key));

    return (
      <div className="export-modal__section">
        <div className="export-modal__section-header">
          <h4>{t(titleKey)}</h4>
          <div className="export-modal__section-actions">
            {allSelected ? (
              <Button
                variant="ghost"
                size="sm"
                className="export-modal__link-button"
                onClick={() => deselectAllInCategory(category)}
              >
                {t('export.deselectAll')}
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="export-modal__link-button"
                onClick={() => selectAllInCategory(category)}
              >
                {t('export.selectAll')}
              </Button>
            )}
          </div>
        </div>
        <div className="export-modal__fields">
          {fields.map(field => (
            <label key={field.key} className="export-modal__field">
              <input
                type="checkbox"
                checked={selectedFields.has(field.key)}
                onChange={() => toggleField(field.key)}
              />
              <span>{getFieldLabel(field)}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || t('export.title')}
      size="medium"
    >
      <div className="export-modal">
        {/* Format Selection */}
        <div className="export-modal__format">
          <FormField htmlFor="export-format" label={t('export.format')}>
            <SearchableSelect
              id="export-format"
              options={[
                { _id: 'xlsx', name: t('export.xlsx') },
                { _id: 'csv', name: t('export.csv') },
              ]}
              value={format}
              onChange={(v) => setFormat((v as ExportFormat) || 'xlsx')}
              allowClear={false}
            />
          </FormField>
        </div>

        {/* Field Selection */}
        <div className="export-modal__fields-container">
          <h3>{t('export.selectFields')}</h3>

          {renderFieldSection(memberFields, 'member', 'export.memberFields')}
          {renderFieldSection(userFields, 'user', 'export.userFields')}
          {renderFieldSection(attendanceFields, 'attendance', 'export.attendanceFields')}
          {renderFieldSection(requirementFields, 'requirement', 'export.requirementFields')}
        </div>

        {/* Error */}
        {error && <div className="export-modal__error">{error}</div>}

        {/* Actions */}
        <div className="export-modal__actions">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleExport}>
            {t('export.exportButton')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
